import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { 
  ShieldCheck, 
  FileText, 
  RefreshCw, 
  Truck, 
  Award,
  ArrowLeft,
  MessageCircle,
  Phone,
  Mail,
  CheckCircle2
} from 'lucide-react';

export default function PolicyPage() {
  const { type = 'privacy' } = useParams();
  const { storeSettings } = useShop();

  const tabs = [
    { id: 'privacy', label: 'Privacy Policy', icon: <ShieldCheck size={16} /> },
    { id: 'terms', label: 'Terms & Conditions', icon: <FileText size={16} /> },
    { id: 'return', label: 'Lifetime Exchange & Returns', icon: <RefreshCw size={16} /> },
    { id: 'shipping', label: 'Insured Armored Transit', icon: <Truck size={16} /> },
    { id: 'about', label: 'Brand Heritage Story', icon: <Award size={16} /> },
  ];

  const currentTab = tabs.find(t => t.id === type) || tabs[0];

  const getPolicyContent = (tabId) => {
    switch (tabId) {
      case 'privacy':
        return {
          title: 'Privacy & Data Protection Policy',
          subtitle: `How ${storeSettings.storeName} guards your identity, insured logistics, and financial privacy.`,
          content: storeSettings.privacyPolicy || 'No privacy policy configured yet.',
          tag: 'Confidentiality Assured'
        };
      case 'terms':
        return {
          title: 'Terms of Sale & Bullion Conditions',
          subtitle: `Bullion market rate indexing, BIS hallmarking certification, and order conditions.`,
          content: storeSettings.termsConditions || 'No terms and conditions configured yet.',
          tag: 'Official Sales Agreement'
        };
      case 'return':
        return {
          title: '15-Day Inspection & Lifetime Exchange',
          subtitle: `Our hallmark 100% buyback promise on pure gold and certified diamonds.`,
          content: storeSettings.returnPolicy || 'No return policy configured yet.',
          tag: '100% Lifetime Promise'
        };
      case 'shipping':
        return {
          title: 'Insured Armored Transit & Delivery Policy',
          subtitle: `Tamper-evident sealed security vaults, armored escort carriers, and transit insurance.`,
          content: storeSettings.shippingPolicy || 'No shipping policy configured yet.',
          tag: 'Zero-Risk Transit'
        };
      case 'about':
        return {
          title: `About ${storeSettings.storeName}`,
          subtitle: storeSettings.tagline || 'Master jewellery purveyors and hallmark artisans.',
          content: storeSettings.aboutStory || `${storeSettings.storeName} is a premier luxury jewellery house renowned for certified purity, transparent pricing, and bespoke bridal masterpieces.`,
          tag: 'Royal Heritage Legacy'
        };
      default:
        return {
          title: 'Store Policies',
          subtitle: 'Official documentation and customer assurances.',
          content: '',
          tag: 'Compliance'
        };
    }
  };

  const activeData = getPolicyContent(currentTab.id);

  return (
    <div style={{ minHeight: '80vh', backgroundColor: '#07070b', color: '#fff', padding: '2rem 1rem 5rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        {/* Breadcrumb Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1.75rem' }}>
          <Link to="/" style={{ color: 'var(--gold-400)', textDecoration: 'none' }}>Home</Link>
          <span>/</span>
          <span style={{ color: '#fff' }}>{activeData.title}</span>
        </div>

        {/* Tab Navigation Bar */}
        <div style={{
          display: 'flex',
          gap: '0.5rem',
          overflowX: 'auto',
          paddingBottom: '0.75rem',
          marginBottom: '2rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
        }}>
          {tabs.map((tab) => {
            const isActive = currentTab.id === tab.id;
            return (
              <Link
                key={tab.id}
                to={`/policy/${tab.id}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.65rem 1.25rem',
                  borderRadius: '10px',
                  backgroundColor: isActive ? 'rgba(212, 175, 55, 0.15)' : '#0c0c14',
                  border: isActive ? '1px solid var(--gold-400)' : '1px solid rgba(255, 255, 255, 0.08)',
                  color: isActive ? 'var(--gold-400)' : 'var(--text-muted)',
                  textDecoration: 'none',
                  fontSize: '0.88rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease'
                }}
              >
                {tab.icon} {tab.label}
              </Link>
            );
          })}
        </div>

        {/* Content Box */}
        <div style={{
          backgroundColor: '#0c0c14',
          borderRadius: '20px',
          border: '1px solid var(--border-gold)',
          padding: '2.5rem',
          boxShadow: '0 15px 40px rgba(0, 0, 0, 0.5)',
          marginBottom: '3rem'
        }}>
          <div style={{ display: 'inline-block', padding: '4px 12px', borderRadius: '999px', backgroundColor: 'rgba(212, 175, 55, 0.1)', border: '1px solid var(--gold-400)', color: 'var(--gold-400)', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
            {activeData.tag}
          </div>

          <h1 style={{ fontSize: '2rem', fontWeight: 800, color: '#fff', marginBottom: '0.5rem' }}>
            {activeData.title}
          </h1>

          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255, 255, 255, 0.08)', paddingBottom: '1.25rem' }}>
            {activeData.subtitle}
          </p>

          <div style={{
            fontSize: '0.95rem',
            lineHeight: 1.8,
            color: '#e2e8f0',
            whiteSpace: 'pre-line'
          }}>
            {activeData.content}
          </div>

          {/* Highlights */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1.25rem',
            marginTop: '2.5rem',
            paddingTop: '2rem',
            borderTop: '1px solid rgba(212, 175, 55, 0.2)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <CheckCircle2 color="var(--gold-400)" size={20} />
              <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>BIS Hallmark Guaranteed</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <CheckCircle2 color="var(--gold-400)" size={20} />
              <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>100% Insured Pan-India</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <CheckCircle2 color="var(--gold-400)" size={20} />
              <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>Official GST Tax Invoiced</span>
            </div>
          </div>
        </div>

        {/* Support Section */}
        <div style={{
          backgroundColor: '#12121e',
          borderRadius: '16px',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '2rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1.5rem'
        }}>
          <div>
            <h3 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#fff', marginBottom: '0.3rem' }}>
              Have questions regarding our terms or custom orders?
            </h3>
            <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
              Our senior concierge team is available 7 days a week to assist you.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a 
              href={`tel:${storeSettings?.supportPhone || '9876543210'}`} 
              className="btn btn-secondary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none' }}
            >
              <Phone size={15} /> Call Desk
            </a>
            <a 
              href={`https://wa.me/${storeSettings?.supportWhatsapp || '919876543210'}`} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="btn btn-gold btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', textDecoration: 'none' }}
            >
              <MessageCircle size={15} /> WhatsApp Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
